/**
 * Agent workspace toolbar visibility by dialing_type × campaign_type.
 *
 * Manual + outbound:    Dial Next, Hangup, Transfer, Manual Call, Mute, Hold (no Ready; no status banner)
 * Manual + inbound/blended: Ready + Dial Next, Hangup, Transfer, Manual Call, Mute, Hold
 * Predictive:           Ready, Hangup, Transfer, Mute, Hold (no Dial Next, no Manual Call)
 */

export function normalizeDialingType(v) {
  const d = String(v || "").trim().toLowerCase();
  if (d === "manual" || d === "predictive" || d === "bot" || d === "ai_bot") return d;
  return d || "";
}

export function normalizeCampaignType(v) {
  const c = String(v || "").trim().toLowerCase();
  if (c === "inbound" || c === "blended" || c === "outbound") return c;
  return "outbound";
}

export function isInboundish(campaignType) {
  const c = normalizeCampaignType(campaignType);
  return c === "inbound" || c === "blended";
}

/**
 * @param {string} dialingType
 * @param {string} campaignType
 * @returns {{
 *   dialingType: string,
 *   campaignType: string,
 *   showStatusBanner: boolean,
 *   showReady: boolean,
 *   showPause: boolean,
 *   showDialNext: boolean,
 *   showManualCall: boolean,
 *   showHangup: boolean,
 *   showTransfer: boolean,
 *   showMute: boolean,
 *   showHold: boolean,
 *   showRecord: boolean,
 *   requireReadyForDial: boolean,
 * }}
 */
export function agentToolbarVisibility(dialingType, campaignType) {
  const dialing = normalizeDialingType(dialingType);
  const ctype = normalizeCampaignType(campaignType);
  const isManual = dialing === "manual";
  const isPredictive = dialing === "predictive";
  const inboundish = isInboundish(ctype);
  const isManualOutbound = isManual && ctype === "outbound";

  return {
    dialingType: dialing,
    campaignType: ctype,
    // Yellow/ready/incall status strip — hidden for Manual Outbound
    showStatusBanner: !isManualOutbound,
    showReady: isPredictive || (isManual && inboundish),
    showPause: isPredictive || (isManual && inboundish),
    // Dial Next is Manual-only — predictive auto-delivers calls when Ready
    showDialNext: isManual,
    showManualCall: isManual,
    showHangup: true,
    showTransfer: true,
    showMute: true,
    showHold: true,
    // Record is shown only when the campaign is On demand (workspace checks mode)
    showRecord: true,
    // Dial Next / Manual Call never require Ready — only blocked while on a live call
    requireReadyForDial: false,
  };
}

/**
 * Whether Dial Next / Manual Call may be used right now.
 * Only disabled while a customer call is connected (or busy / disposition pending).
 * Ready status is not required for any manual campaign type.
 */
export function canUseManualDialActions({
  dialingType,
  campaignType,
  agentStatus: _agentStatus,
  onCall = false,
  dispositionRequired = false,
  busy = false,
} = {}) {
  if (busy || onCall || dispositionRequired) return false;
  if (normalizeDialingType(dialingType) !== "manual") return false;
  const vis = agentToolbarVisibility(dialingType, campaignType);
  if (!vis.showDialNext && !vis.showManualCall) return false;
  return true;
}
